import React, { useState, useMemo, useCallback } from "react";
import {
  Modal,
  TextInput,
  Text,
  Group,
  Box,
  Stack,
  Badge,
  ScrollArea,
  UnstyledButton,
  Divider,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { Travel } from "@/utils/api";
import { AppButton, AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

interface TravelOptionItem {
  id: string;
  label: string;
  details?: string;
  isActive?: boolean;
  isAllOption?: boolean;
  travel?: Travel;
}

export interface TravelPickerModalProps {
  opened: boolean;
  onClose: () => void;

  /** Currently selected travel ID (null = "All Travels" if showAllOption) */
  value: string | null | undefined;

  /** Callback when a travel is selected */
  onSelect: (travelId: string | null, travel?: Travel) => void;

  /** Available travel options to display */
  options: TravelOptionItem[];

  /** Whether to show "All Travels" option */
  showAllOption?: boolean;

  /** Label for "All Travels" option */
  allOptionLabel?: string;
}

// ============================================
// Constants
// ============================================

const ALL_TRAVELS_VALUE = "__all__";

const FUSE_OPTIONS: IFuseOptions<TravelOptionItem> = {
  keys: [
    { name: "label", weight: 0.7 },
    { name: "details", weight: 0.3 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Component
// ============================================

export const TravelPickerModal: React.FC<TravelPickerModalProps> = ({
  opened,
  onClose,
  value,
  onSelect,
  options,
  showAllOption = false,
}) => {
  const { t } = useTranslation(["common"]);
  const [searchValue, setSearchValue] = useState("");

  // Reset search when modal opens
  React.useEffect(() => {
    if (opened) {
      setSearchValue("");
    }
  }, [opened]);

  // Fuse index for fuzzy search
  const fuse = useMemo(() => new Fuse(options, FUSE_OPTIONS), [options]);

  // Filtered options
  const filteredOptions = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return options;
    return fuse.search(search).map((result) => result.item);
  }, [options, fuse, searchValue]);

  // Handle selection
  const handleSelect = useCallback(
    (option: TravelOptionItem) => {
      if (option.isAllOption) {
        onSelect(null, undefined);
      } else {
        onSelect(option.id, option.travel);
      }
      onClose();
    },
    [onSelect, onClose]
  );

  // Determine selected value for checkmark
  const selectedId = useMemo(() => {
    if (value === null && showAllOption) return ALL_TRAVELS_VALUE;
    return value ?? null;
  }, [value, showAllOption]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("common:selectors.travel.modalTitle", {
        defaultValue: "Select Travel",
      })}
      fullScreen
      zIndex={1000}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 60px)",
          padding: 0,
        },
      }}
    >
      {/* Search input */}
      <Box px="md" pt="xs" pb="sm" style={{ flexShrink: 0 }}>
        <TextInput
          placeholder={t("common:selectors.travel.searchPlaceholder", {
            defaultValue: "Search travels...",
          })}
          value={searchValue}
          onChange={(e) => setSearchValue(e.currentTarget.value)}
          leftSection={<AppIcon iconName="Search" size={18} />}
          rightSection={
            searchValue ? (
              <AppButton
                iconName="X"
                iconSize={16}
                onClick={() => setSearchValue("")}
              />
            ) : undefined
          }
          rightSectionPointerEvents="auto"
          size="md"
          autoFocus
        />
      </Box>

      <Divider />

      {/* Travel list */}
      <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
        <Stack gap={0} px="md" py="xs">
          {filteredOptions.map((option) => {
            const isSelected = selectedId === option.id;

            return (
              <UnstyledButton
                key={option.id}
                onClick={() => handleSelect(option)}
                py="sm"
                px="xs"
                style={{ borderRadius: 8 }}
              >
                <Group gap="sm" wrap="nowrap" justify="space-between">
                  <Group
                    gap="sm"
                    wrap="nowrap"
                    align="flex-start"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Box pt={2}>
                      <AppIcon iconName="Route" size={24} />
                    </Box>
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        <Text
                          size="md"
                          fw={option.isAllOption ? 500 : undefined}
                          lineClamp={1}
                          style={{ flex: 1 }}
                        >
                          {option.label}
                        </Text>
                        {option.isActive && (
                          <Badge size="xs" color="green" variant="light">
                            {t("common:selectors.travel.active")}
                          </Badge>
                        )}
                      </Group>
                      {option.details && (
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {option.details}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                  {isSelected && (
                    <AppIcon iconName="Check" size={18} color="blue" />
                  )}
                </Group>
              </UnstyledButton>
            );
          })}

          {/* No results */}
          {filteredOptions.length === 0 && searchValue.trim() && (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              {t("common:form.noResults", {
                defaultValue: "No travels found",
              })}
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Modal>
  );
};